import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material'
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet'
import { Flame, MapPin } from 'lucide-react'
import { apiService } from '../../services/api'
import MetricCard from '../ui/MetricCard'
import HeatmapLayer from './HeatmapLayer'
import debug from '../../utils/debug'
import { useUser } from '../../context/UserContext'
import 'leaflet/dist/leaflet.css'

const SalesMap = () => {
  const { getAccessibleOperators } = useUser()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  
  // All available operators
  const allOperators = [
    { code: 'ORANGE', name: 'Orange', color: '#ff9800' },
    { code: 'PLUS', name: 'Plus', color: '#4caf50' },
    { code: 'TMOBILE', name: 'T-Mobile', color: '#e91e63' },
    { code: 'PLAY', name: 'Play', color: '#9c27b0' }
  ]
  
  // Filter operators based on user access
  const operators = allOperators.filter(op => 
    getAccessibleOperators(allOperators.map(o => o.code)).includes(op.code)
  )
  
  // Set initial operator to first accessible operator
  const [selectedOperator, setSelectedOperator] = useState(operators[0]?.code || 'ORANGE')
  const [selectedMonth, setSelectedMonth] = useState(null) // null = cały rok
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()) // domyślnie bieżący rok
  const [viewMode, setViewMode] = useState('heatmap') // 'heatmap' lub 'markers'

  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ]
  
  // Generate year options (current year and 2 years back)
  const currentYear = new Date().getFullYear()
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      debug.log('SalesMap', `Fetching data for operator: ${selectedOperator}, year: ${selectedYear}, month: ${selectedMonth}`)
      
      const params = { operator: selectedOperator, year: selectedYear }
      if (selectedMonth !== null) {
        params.month = selectedMonth
      }
      
      const response = await apiService.getSalesMapData(params)
      
      if (response.data.error) {
        throw new Error(response.data.error)
      }
      
      console.log('SalesMap - Data received:', response.data)
      setData(response.data)
      
    } catch (err) {
      debug.error('SalesMap', 'Error fetching data:', err)
      setError(`Błąd pobierania danych mapy: ${err.message}`)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [selectedOperator, selectedMonth, selectedYear])

  const getOperatorInfo = (operatorCode) => {
    return operators.find(op => op.code === operatorCode) || operators[0]
  }

  const getCircleProps = (salon) => {
    const sales = salon.sales || 0
    const maxSales = data?.salons ? Math.max(...data.salons.map(s => s.sales)) : 1
    const minRadius = 5
    const maxRadius = 25
    
    // Calculate radius based on sales (logarithmic scale for better visibility)
    const normalizedSales = maxSales > 0 ? Math.log(sales + 1) / Math.log(maxSales + 1) : 0
    const radius = minRadius + (maxRadius - minRadius) * normalizedSales
    
    const operatorInfo = getOperatorInfo(data.operator)
    
    return {
      center: [parseFloat(salon.latitude), parseFloat(salon.longitude)],
      radius: radius,
      pathOptions: {
        color: operatorInfo.color,
        fillColor: operatorInfo.color,
        fillOpacity: sales > 0 ? 0.6 : 0.2,
        weight: 2
      }
    }
  }

  const getHeatmapPoints = () => {
    if (!data || !data.salons) return []
    
    const validSalons = data.salons.filter(salon => salon.latitude && salon.longitude && salon.sales > 0)
    if (validSalons.length === 0) return []
    
    // Use 95th percentile instead of max to avoid outliers skewing the scale
    const salesValues = validSalons.map(s => s.sales).sort((a, b) => a - b)
    const p95Index = Math.floor(salesValues.length * 0.95)
    const maxSales = salesValues[p95Index] || salesValues[salesValues.length - 1]
    
    if (maxSales === 0) return []
    
    return validSalons.map(salon => {
      // Format: [lat, lng, intensity]
      // Intensity normalized to 0-1 range using P95 as max
      // Values above P95 will be capped at 1.0
      const intensity = Math.min(1.0, salon.sales / maxSales)
      return [
        parseFloat(salon.latitude),
        parseFloat(salon.longitude),
        intensity
      ]
    })
  }

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error && !data) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        🗺️ Mapa sprzedaży
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🔍 Filtry analizy
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={selectedOperator}
                  label="Operator"
                  onChange={(e) => setSelectedOperator(e.target.value)}
                >
                  {operators.map((operator) => (
                    <MenuItem key={operator.code} value={operator.code}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          width={12}
                          height={12}
                          borderRadius="50%"
                          sx={{ backgroundColor: operator.color }}
                        />
                        {operator.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Rok</InputLabel>
                <Select
                  value={selectedYear}
                  label="Rok"
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth sx={{ minWidth: 200 }}>
                <InputLabel>Okres</InputLabel>
                <Select
                  value={selectedMonth || ''}
                  label="Okres"
                  onChange={(e) => setSelectedMonth(e.target.value === '' ? null : parseInt(e.target.value))}
                >
                  <MenuItem value="">Cały rok</MenuItem>
                  {monthNames.map((month, index) => (
                    <MenuItem key={index + 1} value={index + 1}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Box>
                <Typography variant="caption" display="block" gutterBottom sx={{ mb: 1 }}>
                  Tryb wyświetlania
                </Typography>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) {
                      setViewMode(newMode)
                    }
                  }}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="heatmap">
                    <Flame size={16} style={{ marginRight: 4 }} />
                    Heatmapa
                  </ToggleButton>
                  <ToggleButton value="markers">
                    <MapPin size={16} style={{ marginRight: 4 }} />
                    Markery
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {data && (
        <>
          {/* Metrics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Łączna sprzedaż"
                value={data.total_sales?.toLocaleString() || 0}
                variant="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Aktywne salony"
                value={`${data.active_salons}/${data.total_salons}`}
                variant="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Średnia sprzedaż"
                value={data.active_salons > 0 ? (data.total_sales / data.active_salons).toFixed(1) : '0'}
                variant="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Operator"
                value={getOperatorInfo(data.operator).name}
                variant="primary"
              />
            </Grid>
          </Grid>

          {/* Period Info */}
          <Alert severity="info" sx={{ mb: 3 }}>
            📅 <strong>Okres analizy:</strong> {data.analysis_period}
            <br />
            🎯 <strong>Operator:</strong> {getOperatorInfo(data.operator).name}
            <br />
            📍 <strong>Salony z lokalizacją:</strong> {data.total_salons} (tylko salony ze współrzędnymi)
          </Alert>

          {/* Map */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {viewMode === 'heatmap' 
                  ? '🔥 Heatmapa sprzedaży - cieplejsze kolory = większa koncentracja sprzedaży'
                  : '📍 Mapa salonów - rozmiar kółka = wielkość sprzedaży'}
              </Typography>
              
              <Box sx={{ height: 600, mb: 2 }}>
                {data.salons && data.salons.length > 0 ? (
                  <MapContainer
                    center={[52.0, 19.0]} // Centrum Polski
                    zoom={6}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    
                    {viewMode === 'heatmap' ? (
                      <HeatmapLayer 
                        points={getHeatmapPoints()}
                        options={{
                          radius: 30,
                          blur: 20,
                          maxZoom: 17,
                          max: 1.0,
                          minOpacity: 0.5
                        }}
                      />
                    ) : (
                      data.salons.map((salon) => {
                        if (!salon.latitude || !salon.longitude) return null
                        
                        const circleProps = getCircleProps(salon)
                        
                        return (
                          <CircleMarker
                            key={salon.id || salon.plant_code}
                            center={circleProps.center}
                            radius={circleProps.radius}
                            pathOptions={circleProps.pathOptions}
                          >
                            <Popup>
                              <Box sx={{ minWidth: 200 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {salon.salon_name || 'Nieznana nazwa'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Plant Code: {salon.plant_code}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Miasto: {salon.city || 'Nieznane'}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  <strong>Sprzedaż: {salon.sales} szt.</strong>
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Okres: {data.analysis_period}
                                </Typography>
                              </Box>
                            </Popup>
                            
                            <Tooltip>
                              <Box>
                                <strong>{salon.salon_name || salon.plant_code}</strong>
                                <br />
                                Sprzedaż: {salon.sales} szt.
                                <br />
                                {salon.city}
                              </Box>
                            </Tooltip>
                          </CircleMarker>
                        )
                      })
                    )}
                  </MapContainer>
                ) : (
                  <Alert severity="warning">
                    Brak danych do wyświetlenia na mapie. Sprawdź czy salony mają zdefiniowane współrzędne geograficzne.
                  </Alert>
                )}
              </Box>

              {/* Legend */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  📖 Legenda:
                </Typography>
                {viewMode === 'heatmap' ? (
                  <>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      <Chip
                        label="🔵 Niebieski = Niska sprzedaż"
                        variant="outlined"
                      />
                      <Chip
                        label="🟢 Zielony = Średnia sprzedaż"
                        variant="outlined"
                      />
                      <Chip
                        label="🟡 Żółty = Wysoka sprzedaż"
                        variant="outlined"
                      />
                      <Chip
                        label="🔴 Czerwony = Bardzo wysoka sprzedaż"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      ℹ️ Skala kolorów oparta na 95. pertycentylu (95% salonów), aby outliers nie zniekształcały wizualizacji
                    </Typography>
                  </>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    <Chip
                      icon={<Box width={8} height={8} borderRadius="50%" sx={{ backgroundColor: getOperatorInfo(data.operator).color }} />}
                      label={`Salony ${getOperatorInfo(data.operator).name}`}
                      variant="outlined"
                    />
                    <Chip
                      label="Większe kółko = więcej sprzedaży"
                      variant="outlined"
                    />
                    <Chip
                      label="Przezroczyste = brak sprzedaży"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  )
}

export default SalesMap
